import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { LocationTree } from '@app/interfaces/rm/location';
import { LocationGroupData, LocationGroups } from '@app/interfaces/rm/resource-instances';
import { SelectOption } from '@app/interfaces/sys/dict';
import { LocationFloorService } from '@services/rm/location-floor.service';
import { LocationLabService } from '@services/rm/location-lab.service';
import { LocationRoomService } from '@services/rm/location-room.service';
import { LocationTypeService } from '@services/rm/location-type.service';
import { LocationService } from '@services/rm/location.service';

@Component({
    selector: 'app-location-group',
    templateUrl: './location-group.component.html',
    styleUrls: ['./location-group.component.less'],
})
export class LocationGroupComponent implements OnInit, OnChanges {
    @Output() ready = new EventEmitter();
    @Input() locationGroupData!: LocationGroupData;
    @Input() colSpan = 24;
    @Input() labelSpan = 6;
    @Input() canEdit = true;
    @Input() isFromResourceTemplate = false;
    @Input() hasLocationText = true;
    loading = false;
    validateForm!: FormGroup;
    labOptions: SelectOption[] = [];
    roomOptions: SelectOption[] = [];
    floorOptions: SelectOption[] = [];
    locationTypeOptions: SelectOption[] = [];
    locationOptions: SelectOption[] = [];
    selectRoomLoading = false;
    editData!: LocationGroups;
    orginLocationData: LocationGroupData = {} as LocationGroupData;
    selectFloorLoading = false;

    constructor(private fb: FormBuilder,
                private labService: LocationLabService,
                private roomService: LocationRoomService,
                private floorService: LocationFloorService,
                private locationTypeService: LocationTypeService,
                private locationService: LocationService, ) {}

    async ngOnInit(): Promise<void> {
       try {
           this.loading = true;
           setTimeout(() => {
               this.ready.emit(false);
           });
           this.validateForm = this.fb.group({
               locationLabId: [null],
               locationFloorId: [null],
               locationRoomId: [null],
               locationTypeId: [null],
               locationIds: [[]],
           });
           await this.fetchLabOptions();
           await this.fetchLocationTypes();
       } finally {
           this.loading = false;
           setTimeout(() => {
               this.ready.emit(true);
           });
       }
    }

    async setLocationData(): Promise<void> {
        try {
            this.loading = true;
            setTimeout(() => {
                this.ready.emit(false);
            });
            if (!this.locationGroupData) {
                return ;
            }
            const { locationLabId, locationFloorId, locationRoomId, locationTypeId, locationIds } = this.locationGroupData;
            if (locationLabId) {
                await this.fetchFloorOptions(locationLabId);
            }
            if (locationLabId && locationRoomId) {
                await this.fetchRoomOptions(locationLabId, locationFloorId);
            }
            if (locationLabId && locationTypeId) {
                await this.fetchLocation(locationLabId, locationFloorId, locationRoomId, locationTypeId);
            }
            this.orginLocationData = {
                locationLabId,
                locationFloorId,
                locationRoomId,
                locationTypeId,
                locationIds: locationIds ? locationIds : [],
            };
            this.validateForm?.reset({...this.orginLocationData});
        } finally {
            this.loading = false;
            setTimeout(() => {
                this.ready.emit(true);
            });
        }
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        const locationGroupData = changes.locationGroupData;
        if (locationGroupData) {
            await this.setLocationData();
        }
    }

    cancelReset(): void {
        this.validateForm?.reset({...this.orginLocationData});
    }

    async changeLab(): Promise<void> {
        this.reset();
        await this.fetchFloorOptions();
    }

    changeFloor(): void {
        this.validateForm.get('locationIds')!.setValue([]);
        this.validateForm.get('locationRoomId')!.setValue(null);
        this.fetchRoomOptions();
    }

    changeRoom(): void {
        this.validateForm.get('locationIds')!.setValue([]);
        this.fetchLocation();
    }

    async fetchLocation(labId?: number, floorId?: number, roomId?: number, typeId?: number | string): Promise<void> {
        this.locationOptions = [];
        let { locationLabId, locationRoomId, locationTypeId, locationFloorId } = this.validateForm.value;
        locationLabId = labId ? labId : locationLabId;
        locationRoomId = roomId ? roomId : locationRoomId;
        locationTypeId = typeId ? typeId : locationTypeId;
        locationFloorId = floorId ? floorId : locationFloorId;
        if (!locationLabId || !locationFloorId || !locationTypeId) {
            return ;
        }
        const params = {
            locationLabId,
            locationFloorId,
            locationRoomId,
            locationTypeId,
        };
        if (!params.locationFloorId) {
            delete params.locationFloorId;
        }
        if (!params.locationRoomId) {
            delete params.locationRoomId;
        }
        const treeData = await this.locationService.findTree(params);
        this.locationOptions = treeData?.map((item) => {
            return this.mapTree(item);
        });
    }

    mapTree(item: LocationTree): LocationTree {
        return Array.isArray(item.children) && item.children.length > 0 ? {
            ...item,
            children: item.children.map((childItem: LocationTree) => this.mapTree(childItem)),
        } : {
            ...item,
            isLeaf: true
        };
    }

    async changeType(): Promise<void> {
        this.validateForm.get('locationIds')!.setValue([]);
        this.fetchLocation();
    }

    async fetchLabOptions(): Promise<void> {
        this.labOptions = await this.labService.options();
    }

    reset(): void {
        this.validateForm.get('locationFloorId')!.setValue(null);
        this.validateForm.get('locationRoomId')!.setValue(null);
        this.validateForm.get('locationIds')!.setValue([]);
    }

    async fetchFloorOptions(labId?: number): Promise<void> {
        this.floorOptions = [];
        const locationLabId = labId ? labId : this.validateForm.value?.locationLabId;
        if (!locationLabId) {
            return;
        }
        try {
            this.selectFloorLoading = true;
            const query = {
                locationLabId,
            };
            this.floorOptions = await this.floorService.options(query);
        } finally {
            this.selectFloorLoading = false;
        }
    }

    async fetchRoomOptions(labId?: number, floorId?: number): Promise<void> {
        this.roomOptions = [];
        const locationLabId = labId ? labId : this.validateForm.value?.locationLabId;
        const locationFloorId = floorId ? floorId : this.validateForm.value?.locationFloorId;
        if (!locationLabId || !locationFloorId) {
            return;
        }
        try {
            this.selectRoomLoading = true;
            const query = {
                locationLabId,
                locationFloorId
            };
            this.roomOptions = await this.roomService.options(query);
        } finally {
            this.selectRoomLoading = false;
        }
    }

    async fetchLocationTypes(): Promise<void> {
        this.locationTypeOptions = await this.locationTypeService.options();
    }

    getParams(): LocationGroups {
        const { locationLabId, locationFloorId, locationRoomId, locationTypeId, locationIds} = this.validateForm.value;
        const params = {
            locationLabId,
            locationFloorId,
            locationRoomId,
            locationTypeId,
            locationId: locationIds?.length > 0 ? (this.isFromResourceTemplate ? locationIds : locationIds?.[locationIds?.length - 1]) : null
        };
        return params;
    }

    getRadioLocationData(): LocationGroupData {
        const { locationLabId, locationFloorId, locationRoomId, locationTypeId, locationIds} = this.validateForm.value;
        const params = {
            locationLabId,
            locationFloorId,
            locationRoomId,
            locationTypeId,
            locationId: locationIds
        };
        return params;
    }

    isHasChangeLocationData(): boolean {
        return Object.keys(this.orginLocationData)?.some(key => {
            const currentValue = this.validateForm.value[key];
            // @ts-ignore
            const originValue = this.orginLocationData[key];
            return currentValue?.toString() !== originValue?.toString() || !currentValue !== !originValue;
        });
    }
}
